import { api } from './api';
import type {
  Tenant,
  User,
  BillingStatus,
  BillingMode,
  FeatureFlags,
  QuotaOverrides,
  Tier,
} from '@traaker/shared';

export interface CreateTenantPayload {
  name: string;
  domain: string;
  tier: Tier;
  ownerEmail: string;
  ownerName: string;
  settings?: {
    quotaOverrides?: QuotaOverrides;
    featureFlags?: FeatureFlags;
  };
  billing: {
    mode: BillingMode;
    amountCents?: number;
    interval?: 'month' | 'year';
    notes?: string;
  };
}

export interface UpdateTenantPayload {
  name?: string;
  domain?: string;
  tier?: Tier;
  settings?: {
    quotaOverrides?: QuotaOverrides;
    featureFlags?: FeatureFlags;
  };
  contractAmountCents?: number;
  contractInterval?: 'month' | 'year';
  contractNotes?: string;
  billingMode?: BillingMode;
}

export const adminService = {
  listTenants: () => api.get<Tenant[]>('/admin/tenants'),
  getTenant: (tenantId: string) =>
    api.get<{ tenant: Tenant; users: User[] }>(`/admin/tenants/${tenantId}`),
  createTenant: (payload: CreateTenantPayload) =>
    api.post<{ tenant: Tenant; user: User }>('/admin/tenants', payload),
  updateTenant: (tenantId: string, payload: UpdateTenantPayload) =>
    api.patch(`/admin/tenants/${tenantId}`, payload),
  setBillingStatus: (tenantId: string, status: BillingStatus) =>
    api.patch(`/admin/tenants/${tenantId}/billing-status`, { status }),
  setFeatureFlags: (tenantId: string, flags: FeatureFlags) =>
    api.patch(`/admin/tenants/${tenantId}/feature-flags`, flags),
  createCheckoutSession: (
    tenantId: string,
    payload: { successUrl: string; cancelUrl: string },
  ) => api.post<{ url: string }>(`/admin/tenants/${tenantId}/checkout-session`, payload),
  ensureStripeCustomer: (tenantId: string) =>
    api.post<{ stripeCustomerId: string }>(`/admin/tenants/${tenantId}/stripe-customer`, {}),
  resendWelcomeEmail: (tenantId: string) =>
    api.post<{ sent: boolean; to: string }>(`/admin/tenants/${tenantId}/resend-welcome`, {}),
  deleteTenant: (tenantId: string, confirmationName: string) =>
    api.delete<{ deleted: boolean; tenantId: string; usersDeleted: number }>(
      `/admin/tenants/${tenantId}`,
      { confirmationName },
    ),
};

/** Customer-facing self-serve billing (Stripe Customer Portal). */
export const billingService = {
  createPortalSession: (returnUrl?: string) =>
    api.post<{ url: string }>('/billing/portal-session', { returnUrl }),
};
